import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

interface CustomerMessagesProps {
  customer: any;
  onClose: () => void;
  userRole: string;
}

const ACTION_ICONS: Record<string, string> = {
  admin_instruct: '📢',
  staff_update: '💬',
  hold_reason: '🔴',
  status_change: '🔄',
  note: '📝'
};

const CustomerMessages: React.FC<CustomerMessagesProps> = ({ customer, onClose, userRole }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = userRole === 'Admin';
  
  const userStr = localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
  const currentUser = userStr ? JSON.parse(userStr) : null;

  const fetchMessages = async () => {
    try {
      setLoading(true);
      // Re-using the getCrmActivities endpoint but for entity_type = 'customer'
      const res = await apiService.makeRequest(`/crm/activities?lead_id=${customer.id}&entity_type=customer`);
      if (res.success) setMessages(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMessages(); }, [customer.id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await apiService.logCrmActivity({
        action_type: isAdmin ? 'admin_instruct' : 'staff_update',
        entity_type: 'customer',
        entity_id: customer.id,
        description: note,
        performed_by: currentUser?.id
      });
      setNote('');
      fetchMessages();
    } catch (e) { alert('Failed to send message'); }
    finally { setSaving(false); }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div 
        className="crm-modal"
        style={{ minWidth: '500px', maxWidth: '620px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="crm-modal-header" style={{ background: 'white', borderBottom: '1px solid var(--crm-border)', padding: '16px 24px' }}>
          <div>
            <h3 style={{ marginBottom: 4 }}>💬 Messages & Instructions</h3>
            <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>
              <strong>{customer.customer_name}</strong> — {customer.phone}
            </div>
          </div>
          <button className="crm-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Timeline (The Chat) */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '24px' }}>
          {loading ? (
            <div className="crm-loading"><div className="crm-spinner"></div> Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="crm-empty-state" style={{ background: 'white', borderRadius: '12px' }}>
              <div className="crm-empty-state-icon">💭</div>
              <h3>No messages yet</h3>
              <p>Send instructions or status updates here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {messages.map((m: any) => {
                const isSystem = m.action_type === 'status_change';
                const fromAdmin = m.action_type === 'admin_instruct';
                
                return (
                  <div key={m.id} style={{ 
                    display: 'flex', 
                    gap: '12px',
                    opacity: isSystem ? 0.8 : 1
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: fromAdmin ? '#E0E7FF' : isSystem ? 'var(--crm-bg)' : 'var(--crm-primary-light)', 
                      color: fromAdmin ? '#4338CA' : isSystem ? '#64748B' : 'var(--crm-primary)',
                      border: `1px solid ${fromAdmin ? '#C7D2FE' : isSystem ? 'var(--crm-border)' : 'var(--crm-primary-border)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '16px', flexShrink: 0
                    }}>
                      {ACTION_ICONS[m.action_type] || '💬'}
                    </div>
                    
                    <div style={{ 
                      flex: 1, 
                      background: 'white', 
                      padding: '12px 16px', 
                      borderRadius: '12px',
                      border: '1px solid var(--crm-border)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: fromAdmin ? '#4338CA' : 'var(--crm-text-primary)' }}>
                          {m.performed_by?.name || 'System Auto-Event'}
                          {m.performed_by?.role && <span style={{ fontWeight: 400, color: 'var(--crm-text-muted)' }}> ({m.performed_by.role})</span>}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                          {formatTime(m.created_at)}
                        </div>
                      </div>
                      <div style={{ 
                        fontSize: '13.5px', 
                        color: 'var(--crm-text-primary)', 
                        lineHeight: 1.5,
                        fontWeight: fromAdmin ? 500 : 400
                      }}>
                        {m.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Chat Input */}
        <form onSubmit={handleSendMessage} style={{ 
          padding: '16px 24px', 
          background: 'white', 
          borderTop: '1px solid var(--crm-border)',
          display: 'flex', 
          gap: '10px' 
        }}>
          <input
            type="text"
            placeholder={isAdmin ? "Send instructions to the team..." : "Add a note or status update..."}
            value={note}
            onChange={e => setNote(e.target.value)}
            style={{ 
              flex: 1, 
              padding: '10px 14px', 
              border: '1.5px solid var(--crm-border)', 
              borderRadius: '24px', 
              fontSize: '13.5px',
              outline: 'none',
              fontFamily: 'Inter, sans-serif'
            }}
          />
          <button 
            type="submit" 
            className="crm-btn crm-btn-primary" 
            disabled={saving || !note.trim()} 
            style={{ padding: '0 20px', borderRadius: '24px' }}
          >
            {saving ? '…' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CustomerMessages;
